import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ShopHeader from '@/components/commerce/ShopHeader';
import ShopFooter from '@/components/commerce/ShopFooter';
import MobileBottomNav from '@/components/commerce/MobileBottomNav';
import CartDrawer from '@/components/commerce/CartDrawer';
import ProductCard from '@/components/commerce/ProductCard';
import { useShopFavorites } from '@/context/ShopFavoritesContext';
import { useShopOrg } from '@/context/ShopOrgContext';
import { Package, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { value: 'name', label: 'Naam A-Z' },
  { value: 'price-asc', label: 'Prijs laag-hoog' },
  { value: 'price-desc', label: 'Prijs hoog-laag' },
  { value: 'newest', label: 'Nieuwste eerst' },
];

const ShopProductsPage = () => {
  const { id: orgId, slug: shopSlug, name: organizationName, logoUrl, footer } = useShopOrg();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { favoriteIds } = useShopFavorites();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchValue, setSearchValue] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [sortBy, setSortBy] = useState('name');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [maxPrice, setMaxPrice] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const favoritesOnly = searchParams.get('favorites') === '1';

  const fetchShop = useCallback(async () => {
    try {
      setLoading(true);
      const [categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/public/commerce/${orgId}/categories`),
        axios.get(`${API}/public/commerce/${orgId}/products`),
      ]);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
      const highestPrice = Math.ceil(Math.max(...productsRes.data.map((p) => p.price), 10));
      setMaxPrice(highestPrice);
    } catch (error) {
      toast.error('Kon producten niet laden');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchShop();
  }, [fetchShop]);

  const allTags = useMemo(() => {
    const set = new Set();
    products.forEach((p) => (p.tags || []).forEach((tag) => set.add(tag)));
    return Array.from(set);
  }, [products]);

  const priceCeiling = useMemo(
    () => Math.ceil(Math.max(...products.map((p) => p.price), 10)),
    [products],
  );

  const toggleTag = (tag) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setInStockOnly(false);
    setMaxPrice(priceCeiling);
    setSelectedTags([]);
    setSearchValue('');
    setSearchParams({});
  };

  useEffect(() => {
    const params = {};
    if (searchValue.trim()) params.q = searchValue.trim();
    if (selectedCategory !== 'all') params.category = selectedCategory;
    if (favoritesOnly) params.favorites = '1';
    setSearchParams(params, { replace: true });
  }, [searchValue, selectedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (favoritesOnly) {
      list = list.filter((p) => favoriteIds.includes(p.id));
    }
    if (selectedCategory !== 'all') {
      list = list.filter((p) => p.category_id === selectedCategory);
    }
    if (inStockOnly) {
      list = list.filter((p) => !p.track_stock || (p.stock ?? 0) > 0);
    }
    if (maxPrice !== null) {
      list = list.filter((p) => (p.sale_price ?? p.price) <= maxPrice);
    }
    if (selectedTags.length > 0) {
      list = list.filter((p) => selectedTags.every((tag) => (p.tags || []).includes(tag)));
    }
    if (searchValue.trim()) {
      const q = searchValue.trim().toLowerCase();
      list = list.filter((p) => [p.title, p.sku, p.short_description, p.description, ...(p.tags || [])]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q)));
    }

    switch (sortBy) {
      case 'price-asc':
        list.sort((a, b) => (a.sale_price ?? a.price) - (b.sale_price ?? b.price));
        break;
      case 'price-desc':
        list.sort((a, b) => (b.sale_price ?? b.price) - (a.sale_price ?? a.price));
        break;
      case 'newest':
        list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      default:
        list.sort((a, b) => a.title.localeCompare(b.title));
    }

    return list;
  }, [products, favoritesOnly, favoriteIds, selectedCategory, inStockOnly, maxPrice, selectedTags, searchValue, sortBy]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-teal-200"></div>
          <p className="text-slate-500">Producten laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20 sm:pb-0">
      <Toaster position="top-right" richColors />
      <ShopHeader organizationName={organizationName} logoUrl={logoUrl} searchValue={searchValue} onSearchChange={setSearchValue} />
      <CartDrawer />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">
            {favoritesOnly ? 'Favorieten' : 'Alle producten'}
          </h1>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Filter sidebar */}
          <aside className="hidden md:block space-y-6">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-900">Filters</h3>
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500 h-auto p-0">
                    Wissen
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase text-slate-400">Categorie</Label>
                  <div className="space-y-1">
                    <button
                      type="button"
                      onClick={() => setSelectedCategory('all')}
                      className={`block text-sm ${selectedCategory === 'all' ? 'text-teal-600 font-medium' : 'text-slate-600'}`}
                    >
                      Alles
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => setSelectedCategory(category.id)}
                        className={`block text-sm ${selectedCategory === category.id ? 'text-teal-600 font-medium' : 'text-slate-600'}`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase text-slate-400">Maximumprijs</Label>
                  <Slider
                    value={[maxPrice ?? priceCeiling]}
                    max={priceCeiling}
                    min={0}
                    step={1}
                    onValueChange={(value) => setMaxPrice(value[0])}
                  />
                  <p className="text-xs text-slate-500">tot € {(maxPrice ?? priceCeiling).toFixed(0)}</p>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox id="in-stock" checked={inStockOnly} onCheckedChange={setInStockOnly} />
                  <Label htmlFor="in-stock" className="text-sm text-slate-600">Alleen op voorraad</Label>
                </div>

                {allTags.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase text-slate-400">Tags</Label>
                    <div className="flex flex-wrap gap-2">
                      {allTags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => toggleTag(tag)}
                          className={`text-xs px-2 py-1 rounded-full border ${selectedTags.includes(tag) ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-200 text-slate-600'}`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* Product grid */}
          <div className="md:col-span-3">
            {filteredProducts.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-8 text-center text-slate-500">
                  <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  Geen producten gevonden met deze filters.
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} onClick={() => navigate(`/${shopSlug}/shop/product/${product.slug}`)} />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <ShopFooter organizationName={organizationName} footer={footer} />
      <MobileBottomNav />
    </div>
  );
};

export default ShopProductsPage;
